import { useMemo } from "react";
import type { Lang } from "../types.ts";
import type { LoadedCatalog } from "../catalog.ts";
import type { ProgressMap } from "../state/store.ts";
import { DEFAULT_CLASS_ID } from "../exam.ts";
import { t } from "../i18n/ui.ts";
import type { PracticeRouteMode } from "../router.ts";
import { tally } from "./FitRing.tsx";
import { FitBars } from "./FitBars.tsx";
import { InstallHint } from "./InstallHint.tsx";

/** Re-exported so callers of this view have one place to import from. */
export type PracticeMode = PracticeRouteMode;

interface Props {
  catalog: LoadedCatalog;
  lang: Lang;
  progress: ProgressMap;
  markedCount: number;
  onExam: () => void;
  onPractice: (mode: PracticeMode) => void;
  onSettings: () => void;
  onReset: () => void;
}

export function HomeView({
  catalog, lang, progress, markedCount, onExam, onPractice, onSettings, onReset,
}: Props) {
  const { overall, mistakeCount } = useMemo(() => {
    // Only the questions this licence class is actually asked, matching the
    // pool the "30 new questions" block draws from.
    const mine = catalog.questions.filter(
      (q) => q.basic || q.classes.includes(DEFAULT_CLASS_ID)
    );
    return {
      overall: tally(mine, progress),
      mistakeCount: mine.filter(
        (q) => progress[String(q.id)]?.lastCorrect === false
      ).length,
    };
  }, [catalog, progress]);

  const items: {
    key: string;
    icon: string;
    label: string;
    hint?: string;
    onClick: () => void;
    disabled?: boolean;
  }[] = [
    {
      key: "exam",
      icon: "🎓",
      label: t(lang, "startExam"),
      hint: t(lang, "startExamHint"),
      onClick: onExam,
    },
    {
      key: "new",
      icon: "30",
      label: t(lang, "newQuestions"),
      hint: t(lang, "newQuestionsHint"),
      onClick: () => onPractice({ kind: "new" }),
    },
    {
      key: "all",
      icon: "≡",
      label: t(lang, "allQuestions"),
      onClick: () => onPractice({ kind: "all" }),
    },
    {
      key: "topics",
      icon: "📋",
      label: t(lang, "specialTopics"),
      hint: t(lang, "specialTopicsHint"),
      onClick: () => onPractice({ kind: "topic", categoryId: null }),
    },
    {
      key: "search",
      icon: "🔍",
      label: t(lang, "searchQuestions"),
      onClick: () => onPractice({ kind: "search" }),
    },
    {
      key: "mistakes",
      icon: "⚠",
      label: t(lang, "lastMistakes", { n: mistakeCount }),
      onClick: () => onPractice({ kind: "mistakes" }),
      disabled: mistakeCount === 0,
    },
    {
      key: "marked",
      icon: "★",
      label: t(lang, "markedQuestions", { n: markedCount }),
      onClick: () => onPractice({ kind: "marked" }),
      disabled: markedCount === 0,
    },
  ];

  return (
    <div className="home">
      <FitBars lang={lang} tally={overall} />

      <div className="home__panel">
        <h2 className="home__heading">{t(lang, "training")}</h2>

        <ul className="menu">
          {items.map((item) => (
            <li key={item.key}>
              <button
                type="button"
                className={`menu__item ${item.key === "exam" ? "menu__item--primary" : ""}`}
                onClick={item.onClick}
                disabled={item.disabled}
              >
                <span className="menu__icon" aria-hidden="true">{item.icon}</span>
                <span className="menu__body">
                  <span className="menu__label">{item.label}</span>
                  {item.hint && <span className="menu__hint">{item.hint}</span>}
                </span>
                <span className="menu__chev" aria-hidden="true">›</span>
              </button>
            </li>
          ))}
        </ul>

        <ul className="menu menu--aside">
          <li>
            <button type="button" className="menu__item" onClick={onSettings}>
              <span className="menu__icon" aria-hidden="true">⚙</span>
              <span className="menu__body">
                <span className="menu__label">{t(lang, "settings")}</span>
                <span className="menu__hint">{t(lang, "settingsHint")}</span>
              </span>
              <span className="menu__chev" aria-hidden="true">›</span>
            </button>
          </li>
        </ul>

        <InstallHint lang={lang} />

        <p className="home__meta">
          {t(lang, "catalogInfo", { from: catalog.validFrom })} ·{" "}
          {t(lang, "questionCount", { n: catalog.questions.length })}
        </p>

        <button type="button" className="btn btn--ghost home__reset" onClick={onReset}>
          {t(lang, "resetProgress")}
        </button>
      </div>
    </div>
  );
}
